import images from '@assets/images/index'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, useDropdownMenu } from '@utils/assessmentReportUtils'
import { Breadcrumb, Flex } from 'antd'
import { useMemo, useState } from 'react'

import { Link, useLocation, useNavigate } from '@suntex.core/nav'
import { Database_Procs_GetAlgebraGoalsMetricPlayers_Item } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { Dropdown, LoaderOverlay, SearchInput } from '@suntex.fim/ui'

import {
  columns,
  hidableSelectLabel,
  schoolColumns
} from './AssessmentReport/AssessmentReportGoalAlgebraByPlayerTable '
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

function AssessmentReportGoalsAlgebraByPlayer() {
  const { api } = useFramework()
  const location = useLocation()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()

  const isSchool =
    login.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')

  const { id, sortingkey, level } = GetAPIparams(isSchool, dropdownFilters)

  const { data: algebraGoalMetricsByPlayer = [], isLoading } = useQuery({
    queryKey: ['assessment', 'goalsAlgebraByPlayer', id],
    queryFn: async () => {
      const response = await api.get_Assessment_GoalsAlgebraByPlayer({
        id: id!
      })
      return response.algebraGoalsMetricPlayers || []
    },
    enabled: id != null
  })
  const memoColumns = useMemo<
    ColumnType<Database_Procs_GetAlgebraGoalsMetricPlayers_Item>[]
  >(() => (isSchool ? schoolColumns() : columns()), [])
  const { table, Table } =
    useAssessmentTable<Database_Procs_GetAlgebraGoalsMetricPlayers_Item>({
      data: algebraGoalMetricsByPlayer,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,

      initialSorting: [{ id: sortingkey, desc: false }],
      hideFirstTwoColumns: false,
      hideFirstColumns: true
    })

  const fileName = isSchool
    ? `${login.schoolName}_AlgebraByTeam`
    : `${team?.name}_AlgebraByPlayer`
  const items = useDropdownMenu({
    table,
    hidableSelectLabel,
    fileName,
    hasColumnChange: isSchool ? ['teamGrade'] : [],
    isSchool,
    setIsLoading: setIsExporting
  })

  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length
  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: any }) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      label: original.playerName || original.playerId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: original.playerId
    })
  )
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <Breadcrumb
          className="print-hidden"
          items={[
            {
              title: 'Team Assessment Reports-Algebra Goals Metric'
            },
            {
              title: (
                <Link
                  to={
                    isSchool
                      ? `/school/${id}/assessment-report/algebra-goals-metric`
                      : `/team/${id}/assessment-report/algebra-goals-metric`
                  }
                >
                  <b>
                    Algebra Goals Metric By {$EntityLevel.describe(level).label}
                  </b>
                </Link>
              )
            }
          ]}
        />
        <AssessmentReportNavigation
          exportButton={
            <div className="export-btn" style={{ display: 'flex' }}>
              <Button
                style={{ marginRight: '10px' }}
                shape="round"
                intent="secondary"
                onClick={() => {
                  if (isSchool) {
                    navigate(
                      `/school/${id}/assessment-report/algebra-goals-metric`
                    )
                  } else {
                    navigate(
                      `/team/${id}/assessment-report/algebra-goals-metric`
                    )
                  }
                }}
              >
                View Report by {$EntityLevel.describe(level).label}
              </Button>
              <Dropdown
                label="Export"
                menu={{ items }}
                icon={<images.ArrowSquareDown />}
                overlayClassName="export-dropdown"
              />
            </div>
          }
          title={`Algebra Goals Metric By ${isSchool ? 'Team' : 'Player'}`}
          displayReport={false}
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />
        <AssessmentReportSummaryDashboard
          dropdownFilters={dropdownFilters}
          isSchool={isSchool}
        />
        {/* result */}
        <div className="main-result">
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {login.entityLevel !== EntityLevel.School && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
        />
      </Flex>
    </>
  )
}

export default AssessmentReportGoalsAlgebraByPlayer
